/**
 * Bosnian strings for the two home screens.
 *
 * Two dashboards, chosen by role — a worker's and a supervisor's — so a few
 * keys look like near-duplicates and are not: "Moje prijave" is the worker's
 * own count, "Otvorene prijave" is the supervisor's queue, and they are
 * different questions that happen to be about the same noun.
 *
 * The status badge on a task row is NOT here. `statusLabel` comes from the
 * server with the task, and the only thing this screen adds is the overdue
 * override beside it.
 */

export const home = {
  // The first name only — `user.name.split(' ')[0]` — which is what makes the
  // interpolation worth having rather than a plain concatenation.
  'home.greeting': 'Dobar dan, {name}',

  // ---- Worker -------------------------------------------------------
  'home.report.title': 'Prijavi kvar ili prijedlog',
  'home.report.text': 'Kvar, incident, bezbjednost ili prijedlog za poboljšanje',
  'home.scan.title': 'Skeniraj QR kod',
  'home.scan.text': 'Otvori prijavu za mašinu pored koje stojiš',

  /*
   * The four tiles, and what each one is actually counting.
   *
   * They look like four counts of the same thing over the same period and are
   * not: the first is windowed to a year, the other three are everything since
   * this person started. The two about proposals also carry the sentence the
   * whole improvement scheme rests on — a refused proposal costs nothing — on
   * the screen the people who file them actually open.
   */
  'home.stat.mySubmissions': 'Moje prijave',
  'home.stat.mySubmissions.explain': 'Vaše prijave iz posljednjih dvanaest mjeseci.',
  'home.stat.inProgress': 'U obradi',
  'home.stat.inProgress.explain':
    'Vaše prijave koje još nisu riješene, bez obzira na to kada ste ih poslali.',
  'home.stat.acceptedProposals': 'Prihvaćeni prijedlozi',
  'home.stat.acceptedProposals.explain':
    'Prijedlozi koje je komisija prihvatila — jedino oni podižu vašu ocjenu na rang listi, a odbijen prijedlog ne oduzima ništa.',
  'home.stat.points': 'Osvojeni poeni',
  'home.stat.points.explain':
    'Poeni koje je komisija dala vašim prihvaćenim prijedlozima; u ocjenu na rang listi ulazi broj prihvaćenih, a ne poeni.',

  'home.recent.title': 'Moje posljednje prijave',
  'home.recent.empty': 'Još niste poslali nijednu prijavu',
  'home.recent.emptyText': 'Kada prijavite kvar ili pošaljete prijedlog, pojaviće se ovdje.',

  // ---- Supervisor ---------------------------------------------------
  'home.newSubmission': 'Nova prijava',
  'home.openQueue': 'Otvori red čekanja',

  'home.stat.openSubmissions': 'Otvorene prijave',
  'home.stat.openSubmissions.hint': 'Sve što još nije riješeno',
  // "Sve" above means the whole plant, not this person's own — the worker's
  // dashboard uses the same word for their own count two screens away.
  'home.stat.openSubmissions.explain': 'Sve otvorene prijave u pogonu, ne samo vaše.',
  'home.stat.overdue': 'Van roka',
  'home.stat.overdue.needsAttention': 'Zahtijeva pažnju danas',
  'home.stat.overdue.allOnTime': 'Sve u roku',
  // A nought here can mean "nothing is late" or "nothing has a deadline", and
  // the tile turns green either way.
  'home.stat.overdue.explain':
    'Otvorene prijave kojima je rok prošao; prijave bez upisanog roka se ovdje ne broje.',
  'home.stat.myTasks': 'Moji zadaci',
  'home.stat.myTasks.explain': 'Zadaci dodijeljeni vama koji još nisu završeni.',
  'home.stat.fiveSThisMonth': '5S ovaj mjesec',
  'home.stat.fiveS.open': 'Otvori',
  'home.stat.fiveS.hint': 'Ocjena radnih centara',
  'home.stat.fiveS.notAllowed': 'Nemate pravo ocjenjivanja',

  /*
   * Three forms, and the verb moves with the noun: "1 prijava **je** van roka"
   * against "2 prijave **su** van roka". That is exactly why this is one
   * template per form rather than a count glued in front of a fixed phrase —
   * the old code interpolated `plural(n, 'prijava je', 'prijave su', …)` into
   * the middle of a sentence, which works here and cannot survive a language
   * that puts the number last.
   */
  'home.overdue.one': '{count} prijava je van roka.',
  'home.overdue.few': '{count} prijave su van roka.',
  'home.overdue.many': '{count} prijava je van roka.',
  'home.overdue.view': 'Pogledaj',

  'home.module.queue': 'Red čekanja',
  'home.module.queue.text': 'Neriješene prijave po prioritetu',
  'home.module.tasks': 'Zadaci',
  'home.module.tasks.text': 'Šta je dodijeljeno vama i vašem timu',
  'home.module.fiveS': '5S evaluacija',
  'home.module.fiveS.text': 'Ocjena radnog centra po kriterijumima',
  'home.module.analytics': 'Analitika',
  'home.module.analytics.text': 'Trendovi, usko grlo i 5S rezultati',
  'home.module.admin': 'Administracija',
  'home.module.admin.text': 'Korisnici, šifarnici i dnevnik izmjena',

  'home.queue.title': 'Otvorene prijave',
  'home.queue.empty': 'Nema otvorenih prijava',
  'home.queue.emptyText': 'Sve prijave su riješene ili zatvorene.',

  // ---- Shared cards -------------------------------------------------
  'home.tasks.title': 'Moji zadaci',
  'home.tasks.all': 'Svi zadaci →',
  'home.tasks.due': 'Rok: {date}',
  'home.tasks.noDue': 'Bez roka',
  'home.tasks.overdue': 'Van roka',
  'home.submissions.all': 'Sve prijave →',
  'home.fiveS.title': 'Centri koje održavaš',
  // Context, not a ranking: nobody is told where they stand relative to anyone.
  'home.fiveS.plant': 'Prosjek pogona',
  // Said out loud, because a second number beside your own on a screen that
  // also carries a public ranking is read as a position until it says it is not.
  'home.fiveS.explain':
    'Prosjek pogona pored stoji samo da imate s čim uporediti svoj broj — to nije rang i nigdje ne piše na kojem ste mjestu.',

  // ---- The rating, and the ranking everybody can see ----------------
  'leaderboard.title': 'Rang lista',
  // The formula, printed. A number people are ranked by has to be arguable,
  // and it cannot be argued with unless it says what it is made of.
  'leaderboard.formula':
    'Osnova: 5S {fiveS} i završeni zadaci {tasks}. Prihvaćeni prijedlozi dodaju do +{bonus} bodova ({accepted} prihvaćena prijedloga za punu premiju) — prijedlozi mogu samo podići ocjenu, nikada je ne smanjuju.',
  /*
   * The three things the printed formula cannot say in one line.
   *
   * It states the weights and the size of the bonus; it does not state what the
   * 5S half is a fact *about*, what a refused proposal costs, or what happens
   * when somebody has no walks at all. All three are the difference between a
   * public ranking people can argue with and one they can only resent — which
   * is why this is a note on the screen rather than a tooltip nobody opens.
   *
   * Longer than the guide's version of the same rules on purpose: `/uputstvo`
   * is read once, this is read beside the number.
   */
  'leaderboard.explain.note':
    'Dio za 5S govori o mašinama koje je neko održavao, a ne o njemu — ko je prešao na zapušten centar ima nizak procenat upravo zbog toga. Prihvaćeni prijedlozi se dodaju na osnovu i mogu je samo podići: odbijen prijedlog ne oduzima ništa. Ko nije bio ni na jednom obilasku ili nema nijedan zadatak, ocjenjuje se samo po onome što ima, a dio koji nedostaje se ne računa kao nula.',
  // Standard competition ranking, in the one form anybody can check against the
  // list in front of them.
  'leaderboard.explain.ties':
    'Isti rezultat dijeli isto mjesto: dvoje sa 82 su oboje drugi, a sljedeći je četvrti.',
  'leaderboard.part.fiveS': '5S',
  'leaderboard.part.bonus': '+{bonus} za {accepted} prihvaćena',
  'leaderboard.part.followThrough': 'Zadaci',
  'leaderboard.you': '(vi)',
  'leaderboard.thin': 'malo obilazaka',
  'leaderboard.showAll': 'Prikaži sve ({count})',
  'leaderboard.showLess': 'Prikaži manje',
  'leaderboard.rating': 'Ocjena',
  'leaderboard.position': 'Mjesto',
} as const

export type HomeSlice = Record<keyof typeof home, string>
